/**
 * React binding for the WebHID transport.
 *
 * The app's only AUTOMATIC device transition is STOP (AGENTS.md section 10.4): the
 * device auto-starts on power-up by design, so connecting must make it quiet, and
 * starting is always an explicit user action.
 *
 * ---------------------------------------------------------------------------
 * RECONNECT IS THE HARD PART. Every rule below exists because of an observed bug:
 *
 *  - **Use `event.device` from the `connect` event, never a `getDevices()` scan.**
 *    The event carries the REAL new handle. `getDevices()` can still report the
 *    stale one from before the replug, and opening a stale handle HANGS -- which is
 *    the "stuck opening the device" symptom.
 *
 *  - **Tear the old transport down before attaching a new one.** Otherwise the
 *    previous HIDDevice stays open in Chrome, still holding its inputreport
 *    listener, and two live transports race for the same responses.
 *
 *  - **Serialise attaches.** `connect` can fire more than once (and can fire while a
 *    manual Connect is in flight). Two overlapping `open()` calls is another way to
 *    hang.
 *
 *  - **Retry the first handshake.** On a fresh plug the device may still be
 *    finishing enumeration, so a single 1 s window is easy to lose -- "msgId 0x02
 *    timed out" was exactly that.
 * ---------------------------------------------------------------------------
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Transport } from './transport.ts';
import { JigState, isOurDevice, shouldAttach } from '../protocol/constants.ts';
import type { JigStateValue } from '../protocol/types.ts';
import type { DeviceConfig, DeviceInfo, DeviceStatus } from '../protocol/types.ts';

export type ConnectionPhase =
  | 'unsupported'   // no WebHID in this browser
  | 'insecure'      // not a secure context
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface HidDeviceState {
  phase: ConnectionPhase;
  /**
   * Why, when phase is 'unsupported' / 'insecure' / 'error'.
   *
   * Typed `string | undefined` rather than `string` because
   * `exactOptionalPropertyTypes` is on: an optional property may be ABSENT, but
   * assigning an explicit `undefined` to it is otherwise an error.
   */
  message?: string | undefined;
  transport: Transport | null;
  info: DeviceInfo | null;
  config: DeviceConfig | null;
  status: DeviceStatus | null;
  linkVerified: boolean;
}

const INITIAL: HidDeviceState = {
  phase: 'disconnected',
  transport: null,
  info: null,
  config: null,
  status: null,
  linkVerified: false,
};

function detectEnvironment(): { phase: ConnectionPhase; message?: string } {
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return {
      phase: 'insecure',
      message:
        'WebHID needs a secure context. Load this page over HTTPS, or over http://localhost during development.',
    };
  }
  if (typeof navigator === 'undefined' || !('hid' in navigator)) {
    return {
      phase: 'unsupported',
      message:
        'This browser does not implement WebHID. Use Chrome or Edge 89+. Firefox and Safari cannot talk to the device at all.',
    };
  }
  return { phase: 'disconnected' };
}

export function useHidDevice() {
  const [state, setState] = useState<HidDeviceState>(() => ({
    ...INITIAL,
    ...detectEnvironment(),
  }));

  /** The live transport, or null. The single source of truth for "what is attached". */
  const transportRef = useRef<Transport | null>(null);
  /** In-flight attach, so concurrent connect events cannot race. */
  const attachingRef = useRef<Promise<void> | null>(null);

  /* --------------------------------------------------------------- detach -- */

  const detach = useCallback(async () => {
    const previous = transportRef.current;
    transportRef.current = null;
    if (!previous) return;
    previous.dispose();
    await previous.close();
  }, []);

  /* --------------------------------------------------------------- attach -- */

  /**
   * Attach to a transport and bring the app up on it.
   *
   * NEVER THROWS, and always leaves the phase in a state the user can act on. That
   * matters because this runs from two places -- an explicit Connect and the
   * `connect` event -- and an auto-attach that failed silently would leave the UI on
   * "Opening the device…" forever with no error and no way out.
   */
  const attach = useCallback(
    async (transport: Transport): Promise<void> => {
      // Serialise: a second connect event waits for the first attach to finish
      // rather than opening the same device twice.
      if (attachingRef.current) {
        await attachingRef.current;
      }

      const run = (async () => {
        setState((s) => ({ ...s, phase: 'connecting', transport, message: undefined }));

        // ALWAYS tear the old one down first. A stale open HIDDevice keeps its
        // listener and races the new one for replies.
        await detach();

        await transport.open();

        // Park the device before anything else. It auto-starts 5 s after power-up,
        // so the first thing a connected app does is stop it -- before rendering
        // controls, before the first status poll. STOP is idempotent, so doing it
        // again on a reconnect is free.
        await transport.stop();

        // `hello()` retries internally: on a fresh plug the device may still be
        // finishing enumeration and a single 1 s window is easy to lose.
        const info = await transport.hello();
        const [config, status] = await Promise.all([
          transport.getConfig(),
          transport.getStatus(),
        ]);
        await transport.ping();

        transportRef.current = transport;
        setState({ phase: 'connected', transport, info, config, status, linkVerified: true });
      })();

      // Keep a stable reference so the cleanup below can actually match it.
      // (`run.catch(...)` builds a NEW promise each call, so comparing against a
      // fresh one never matches and the guard is never released.)
      const guarded = run.catch((error: unknown) => {
        transport.dispose();
        setState((s) => ({
          ...s,
          phase: 'error',
          message:
            error instanceof Error
              ? error.message
              : `Could not open the device: ${String(error)}`,
        }));
      });

      attachingRef.current = guarded;
      try {
        await guarded;
      } finally {
        if (attachingRef.current === guarded) attachingRef.current = null;
      }
    },
    [detach],
  );

  /* -------------------------------------------------------------- actions -- */

  const connect = useCallback(async () => {
    let transport: Transport | undefined;
    try {
      transport = (await Transport.findAlreadyGranted()) ?? (await Transport.request());
    } catch (error) {
      setState((s) => ({
        ...s,
        phase: 'error',
        message: error instanceof Error ? error.message : String(error),
      }));
      return;
    }

    if (!transport) {
      setState((s) => ({ ...s, phase: 'disconnected', message: 'No device selected.' }));
      return;
    }

    // attach() handles its own errors and never throws.
    await attach(transport);
  }, [attach]);

  const disconnect = useCallback(async () => {
    await detach();
    setState((s) => ({ ...s, ...INITIAL, phase: detectEnvironment().phase }));
  }, [detach]);

  /** Re-send STOP for a device that was replugged mid-session. */
  const repark = useCallback(async () => {
    const transport = transportRef.current;
    if (transport?.isOpen) {
      await transport.stop().catch(() => undefined);
    }
  }, []);

  /**
   * Re-read everything from the device.
   *
   * RETURNS the status it just read, deliberately. Callers that need to assert the
   * device's state cannot read `state.status` after awaiting this, because React has
   * not flushed the update yet; they would be reading the PREVIOUS poll's value. See
   * `stopJiggle()` and `ensureStopped()` in App.tsx.
   */
  const refresh = useCallback(async (): Promise<DeviceStatus | null> => {
    const transport = transportRef.current;
    if (!transport?.isOpen) return null;
    try {
      const [info, config, status] = await Promise.all([
        transport.getInfo(),
        transport.getConfig(),
        transport.getStatus(),
      ]);
      setState((s) => ({ ...s, info, config, status }));
      return status;
    } catch (error) {
      // A single failed poll is usually a lost race, not a dead link. Only surface
      // it if the device has actually gone away, so the UI does not flash errors
      // during normal operation.
      if (!transport.isOpen) {
        setState((s) => ({
          ...s,
          phase: 'error',
          message: 'The device went away. Replug it, or click Try again.',
        }));
      } else if (error instanceof Error && /not open|closed|disposed/.test(error.message)) {
        setState((s) => ({ ...s, phase: 'error', message: error.message }));
      }
      return null;
    }
  }, []);

  /* ------------------------------------------------ live status polling -- */

  useEffect(() => {
    if (state.phase !== 'connected') return;
    const id = setInterval(() => void refresh(), 1000);
    return () => clearInterval(id);
  }, [state.phase, refresh]);

  /* -------------------------------------------------------- connect events -- */

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('hid' in navigator)) return;

    const onConnect = (event: HIDConnectionEvent) => {
      // The event's own device is the REAL new handle. Scanning getDevices() can
      // hand back the stale one from before the replug, and opening that hangs.
      if (!shouldAttach(event.device)) return; // the mouse interface, or not ours
      const transport = Transport.fromDevice(event.device);
      if (!transport) return;
      // attach() sets the error state itself, so a failed auto-attach is visible
      // rather than leaving the UI on "Opening the device…".
      void attach(transport);
    };

    const onDisconnect = (event: HIDConnectionEvent) => {
      // Only react to OUR interface going away. Matched on vendor/product, NOT on
      // collections: a disconnected HIDDevice has already lost its report descriptor,
      // so the collection check would be false and we would ignore our own unplug.
      if (!isOurDevice(event.device)) return;
      const current = transportRef.current;
      if (!current) return;
      current.dispose();
      transportRef.current = null;
      setState((s) => ({ ...s, ...INITIAL, phase: 'disconnected' }));
    };

    navigator.hid.addEventListener('connect', onConnect);
    navigator.hid.addEventListener('disconnect', onDisconnect);
    return () => {
      navigator.hid.removeEventListener('connect', onConnect);
      navigator.hid.removeEventListener('disconnect', onDisconnect);
    };
  }, [attach]);

  useEffect(() => {
    return () => {
      transportRef.current?.dispose();
    };
  }, []);

  /* -------------------------------------------------------------- derived -- */

  const isRunning = state.status?.state === JigState.RUNNING;
  const isParked = state.status?.state === JigState.STOPPED;
  const needsParking =
    state.status?.state === JigState.BOOT_WAIT || state.status?.state === JigState.RUNNING;

  const stateName: JigStateValue | null = state.status?.state ?? null;

  const startJiggle = useCallback(async () => {
    await transportRef.current?.start();
    return refresh();
  }, [refresh]);

  const stopJiggle = useCallback(async () => {
    await transportRef.current?.stop();
    return refresh();
  }, [refresh]);

  return {
    ...state,
    stateName,
    isRunning,
    isParked,
    needsParking,
    connect,
    disconnect,
    repark,
    refresh,
    startJiggle,
    stopJiggle,
  };
}
